import React, { useEffect, useRef, useState } from 'react';
import { Button, Grid, InputBase, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import PublishIcon from '@material-ui/icons/Publish';
import GetAppIcon from '@material-ui/icons/GetApp';
import ClearIcon from '@material-ui/icons/Clear';
import { SearchOutlined as SearchIcon } from '@material-ui/icons';
import Breadcrums from '../../components/BreadCrums/breadcrums';
import TopSpacer from '../../components/Common/spacer/TopSpacer';
import SearchGridSSP from '../../components/table/SearchGrid_SSP';
import Loader from '../../components/Loader/Loader';
import { ActionDialog } from '../../components/ActionDialog/ActionDialog';
import { withSnackbar } from '../../components/Message/Alert';
import { PostDataAPI } from '../../Services/APIService';
import AddInventory from './addInventory/AddInventory';
import BulkUpload from './bulkUpload/BulkUpload';
import ExportInventory from './exportInventory/ExportInventory';
import AssignmentHistory from './assignmentHistory/AssignmentHistory';
import UnassignDevice from './unassignDevice/UnassignDevice';
import { formatDateTime } from '../../components/Common/Extensions';
import useStyles from './styles';

const SEARCH_EXTRA_PARAM_INDEX = 2;
const ITEM_TYPE_EXTRA_PARAM_INDEX = 3;

function Inventory({ showMessage }) {
    const classes = useStyles();
    const [inventoryData, setInventoryData] = useState([]);
    const [searchFilter, setSearchFilter] = useState('');
    const searchDebounceRef = useRef(null);
    const [addInventoryDialog, setAddInventoryDialog] = useState(false);
    const [bulkUploadDialog, setBulkUploadDialog] = useState(false);
    const [exportInventoryDialog, setExportInventoryDialog] = useState(false);
    const [assignmentHistoryDialog, setAssignmentHistoryDialog] = useState(false);
    const [assignmentHistory, setAssignmentHistory] = useState([]);
    const [unassignDeviceDialog, setUnassignDeviceDialog] = useState(false);
    const [selectedDevice, setSelectedDevice] = useState({});
    const [editItem, setEditItem] = useState({});
    const [isDataGridUpdate, setIsDataGridUpdate] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [itemTypeOptions, setItemTypeOptions] = useState([]);
    const [selectedItemType, setSelectedItemType] = useState(null);
    const [gridRequest, setGridRequest] = useState({
        pageNumber: 1,
        pageSize: 10,
        sortColumn: '',
        sortOrder: '',
        extraParams: ['', '', '', ''],
        language: '',
    });
    const [actiondialogState, setActionDialogState] = useState({
        showHide: false,
        type: 'confirm',
        message: '',
    });

    const loadInventoryGrid = (request = gridRequest) => {
        setIsLoading(true);
        PostDataAPI('deviceinventory/loadDeviceGrid', request).then(result => {
            if (result.success) {
                const records = (result.data || []).map(item => ({
                    ...item,
                    imei1: item.imei1 || '-',
                    imei2: item.imei2 || '-',
                    iccid: item.iccid || '-',
                    cartonNo: item.cartonNo || '-',
                    itemTypeName: item.itemTypeName || '-',
                    createdByName: item.createdByName || '-',
                    updatedByName: item.updatedByName || '-',
                }));
                setInventoryData(records);
                setTotalRows(result.dataCount || 0);
                setIsDataGridUpdate(previous => !previous);
            } else {
                setInventoryData([]);
                setTotalRows(0);
                showMessage('Error', result.message || 'Error loading inventory, please contact administrator', 'error', 3000);
            }
            setIsLoading(false);
        }).catch(() => {
            setIsLoading(false);
            showMessage('Error', 'Error loading inventory, please contact administrator', 'error', 3000);
        });
    };

    useEffect(() => {
        loadInventoryGrid();
        PostDataAPI('deviceinventory/getItemTypeList', {}).then(result => {
            if (result.success) {
                setItemTypeOptions((result.data || [])
                    .filter(item => item.isActive === true)
                    .map(item => ({ label: item.name, value: item.itemTypeId })));
            } else {
                showMessage('Error', result.message || 'Error loading item types, please contact administrator', 'error', 3000);
            }
        }).catch(() => {
            showMessage('Error', 'Error loading item types, please contact administrator', 'error', 3000);
        });
        // The initial request is intentionally loaded once on page entry.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => () => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
    }, []);

    const handleItemTypeChange = (event, option) => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        setSelectedItemType(option);
        const extraParams = [...gridRequest.extraParams];
        extraParams[SEARCH_EXTRA_PARAM_INDEX] = searchFilter.trim().length >= 3
            ? searchFilter.trim()
            : extraParams[SEARCH_EXTRA_PARAM_INDEX] || '';
        extraParams[ITEM_TYPE_EXTRA_PARAM_INDEX] = option ? String(option.value) : '';
        const request = {
            ...gridRequest,
            pageNumber: 1,
            extraParams,
        };
        setGridRequest(request);
        loadInventoryGrid(request);
    };

    const handleSearchChange = value => {
        setSearchFilter(value);
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }

        const searchValue = value.trim();
        if (searchValue.length > 0 && searchValue.length < 3) {
            return;
        }

        const loadSearchResults = () => {
            const extraParams = [...gridRequest.extraParams];
            extraParams[SEARCH_EXTRA_PARAM_INDEX] = searchValue;
            const request = {
                ...gridRequest,
                pageNumber: 1,
                extraParams,
            };
            setGridRequest(request);
            loadInventoryGrid(request);
        };

        if (!searchValue) {
            loadSearchResults();
            return;
        }

        searchDebounceRef.current = setTimeout(loadSearchResults, 400);
    };

    const handleGridChange = (page, pageSize, isClearSearch, sortColumn, sortOrder) => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
        }
        const extraParams = [...gridRequest.extraParams];
        const currentSearchValue = searchFilter.trim();
        if (!currentSearchValue || currentSearchValue.length >= 3) {
            extraParams[SEARCH_EXTRA_PARAM_INDEX] = currentSearchValue;
        }
        const isPageSizeChange = pageSize !== gridRequest.pageSize;
        const request = {
            ...gridRequest,
            pageNumber: isPageSizeChange ? 1 : page,
            pageSize: pageSize,
            sortColumn: sortColumn || '',
            sortOrder: sortOrder || '',
            extraParams,
        };
        setGridRequest(request);
        loadInventoryGrid(request);
    };

    const closeInventoryDialog = () => {
        setEditItem({});
        setAddInventoryDialog(false);
    };

    const handleSuccessClose = (message) => {
        closeInventoryDialog();
        showMessage('Success', message, 'success', 3000);
        loadInventoryGrid(gridRequest);
    };

    const handleBulkUploadSuccess = (message) => {
        setBulkUploadDialog(false);
        showMessage('Success', message, 'success', 3000);
        loadInventoryGrid(gridRequest);
    };

    const showActionDialog = (message, onConfirm) => {
        setActionDialogState({
            showHide: true,
            type: 'confirm',
            message: message,
            onClickOk: onConfirm,
        });
    };

    const deleteInventory = (item) => {
        showActionDialog('Are you sure you want to delete this record?', () => {
            setIsLoading(true);
            PostDataAPI('deviceinventory/deleteDevice', { deviceId: item.deviceId }, true).then(result => {
                if (result.success) {
                    const fallbackPage = inventoryData.length === 1 && gridRequest.pageNumber > 1
                        ? gridRequest.pageNumber - 1
                        : gridRequest.pageNumber;
                    const request = { ...gridRequest, pageNumber: fallbackPage };
                    setGridRequest(request);
                    showMessage('Success', result.message || 'Inventory deleted successfully.', 'success', 3000);
                    loadInventoryGrid(request);
                } else {
                    setIsLoading(false);
                    showMessage('Error', result.message || 'Error deleting inventory, please contact administrator', 'error', 3000);
                }
            }).catch(() => {
                setIsLoading(false);
                showMessage('Error', 'Error deleting inventory, please contact administrator', 'error', 3000);
            });
        });
    };

    const editInventory = (item) => {
        setEditItem(item);
        setAddInventoryDialog(true);
    };

    const handleAssignmentHistory = (item) => {
        setIsLoading(true);
        PostDataAPI('deviceinventory/getAssignmentHistory', { deviceId: item.deviceId }).then(result => {
            setIsLoading(false);
            if (result.success) {
                setAssignmentHistory((result.data || []).map(history => ({
                    ...history,
                    status: history.status || '-',
                    applicantName: history.applicantName || '-',
                    program: history.program || '-',
                    matchedImei: history.matchedImei || '-',
                    applicationId: history.applicationId === null || history.applicationId === undefined ? '-' : history.applicationId,
                    assignedDate: history.assignedDate ? formatDateTime(history.assignedDate) : '-',
                    unassignedDate: history.unassignedDate ? formatDateTime(history.unassignedDate) : '-',
                    reason: history.reason || '-',
                    assignedByName: history.assignedByName || '-',
                    unassignedByName: history.unassignedByName || '-',
                })));
                setAssignmentHistoryDialog(true);
            } else {
                showMessage('Error', result.message || 'Error loading assignment history, please contact administrator', 'error', 3000);
            }
        }).catch(() => {
            setIsLoading(false);
            showMessage('Error', 'Error loading assignment history, please contact administrator', 'error', 3000);
        });
    };

    const handleUnassignDevice = (item) => {
        setSelectedDevice(item);
        setUnassignDeviceDialog(true);
    };

    const closeUnassignDeviceDialog = () => {
        setUnassignDeviceDialog(false);
        setSelectedDevice({});
    };

    const handleUnassignSuccess = (message) => {
        closeUnassignDeviceDialog();
        showMessage('Success', message, 'success', 3000);
        loadInventoryGrid(gridRequest);
    };

    const handleInventoryAction = (action, item) => {
        if (action === 'edit') {
            editInventory(item);
        } else if (action === 'delete') {
            deleteInventory(item);
        } else if (action === 'assignmentHistory') {
            handleAssignmentHistory(item);
        } else if (action === 'unassignDevice') {
            handleUnassignDevice(item);
        }
    };

    return (
        <>
            {isLoading ? <Loader /> : ''}
            <TopSpacer />
            <div className={classes.header}>
                <Grid container alignItems="baseline">
                    <Grid item lg={12}>
                        <Breadcrums parentLink="Inventory" isBack={true} />
                    </Grid>
                </Grid>
            </div>

            <Grid row className={classes.gridHeader}>
                <div className={classes.itemTypeFilter}>
                    <Autocomplete
                        options={itemTypeOptions}
                        getOptionLabel={option => option.label || ''}
                        getOptionSelected={(option, value) => option.value === value.value}
                        value={selectedItemType}
                        onChange={handleItemTypeChange}
                        renderInput={params => (
                            <TextField {...params} placeholder="Select Item Type" variant="outlined" size="small" />
                        )}
                    />
                </div>
                <div className={classes.gridActions}>
                    <Button className={classes.changeBtn} onClick={() => setExportInventoryDialog(true)}>
                        <GetAppIcon className={classes.addIcon} /> Inventory Report
                    </Button>
                    <Button className={classes.changeBtn} onClick={() => setBulkUploadDialog(true)}>
                        <PublishIcon className={classes.addIcon} /> Bulk Upload
                    </Button>
                    <Button className={classes.changeBtn} onClick={() => setAddInventoryDialog(true)}>
                        <AddCircleOutlineIcon className={classes.addIcon} /> Add New
                    </Button>
                </div>
                <div className={classes.gridActions}>
                    <InputBase
                        value={searchFilter}
                        placeholder="Search"
                        className={classes.inventorySearch}
                        startAdornment={<SearchIcon />}
                        endAdornment={searchFilter ? <ClearIcon className={classes.clearIcon} onClick={() => handleSearchChange('')} /> : ''}
                        onChange={event => handleSearchChange(event.target.value)}
                        autoComplete="off"
                    />
                </div>
            </Grid>

            <Grid row className={classes.container}>
                <SearchGridSSP
                    columns="inventory"
                    list={inventoryData}
                    totalRows={totalRows}
                    noRecordMsg="No record exists"
                    Icon={true}
                    isUpdate={isDataGridUpdate}
                    onEditClick={editInventory}
                    onDeleteClick={deleteInventory}
                    onActionClick={handleInventoryAction}
                    isTopPagination={true}
                    handlePageChange={handleGridChange}
                    currentPageNumber={gridRequest.pageNumber}
                />
            </Grid>

            {addInventoryDialog ? (
                <AddInventory
                    dialogOpenClose={addInventoryDialog}
                    handleClose={closeInventoryDialog}
                    handleSuccessClose={handleSuccessClose}
                    data={editItem}
                />
            ) : ''}

            {bulkUploadDialog ? (
                <BulkUpload
                    dialogOpenClose={bulkUploadDialog}
                    handleClose={() => setBulkUploadDialog(false)}
                    handleSuccessClose={handleBulkUploadSuccess}
                />
            ) : ''}

            {exportInventoryDialog ? (
                <ExportInventory
                    dialogOpenClose={exportInventoryDialog}
                    handleClose={() => setExportInventoryDialog(false)}
                />
            ) : ''}

            {assignmentHistoryDialog ? (
                <AssignmentHistory
                    dialogOpenClose={assignmentHistoryDialog}
                    handleClose={() => setAssignmentHistoryDialog(false)}
                    history={assignmentHistory}
                />
            ) : ''}

            {unassignDeviceDialog ? (
                <UnassignDevice
                    dialogOpenClose={unassignDeviceDialog}
                    handleClose={closeUnassignDeviceDialog}
                    handleSuccessClose={handleUnassignSuccess}
                    device={selectedDevice}
                />
            ) : ''}

            <ActionDialog
                type={actiondialogState.type}
                message={actiondialogState.message}
                actiondialogOpenClose={actiondialogState.showHide}
                onSubmit={actiondialogState.onClickOk}
                onCancel={() => setActionDialogState(previous => ({ ...previous, showHide: false }))}
                onClose={() => setActionDialogState(previous => ({ ...previous, showHide: false }))}
            />
        </>
    );
}

export default withSnackbar(Inventory);
